/**
 * Per-affiliate-system guidance shared by the link editors on /advertisers and
 * /users/:id: which /help section documents tracking links, the params a tracking
 * link needs, and where a new campaign key comes from once one is taken.
 */

// Systems we have per-section docs for. Anything else falls back to the Help
// index (no ?section= param).
const HELP_SLUG_BY_SYSTEM = {
    myaffiliates: 'links-myaffiliates',
    referon: 'links-referon',
    cellxpert: 'links-cellxpert',
    softswiss: 'links-softswiss',
    affise: 'links-affise',
    netrefer: 'links-netrefer',
    netreferapi: 'links-netrefer',
};

export function normalizeSystem(system) {
    if (!system) return '';
    return String(system).toLowerCase().replace(/\s+/g, '');
}

export function hasHelpSection(system) {
    return !!HELP_SLUG_BY_SYSTEM[normalizeSystem(system)];
}

export function helpButtonTooltip(system) {
    if (hasHelpSection(system)) return `How to add a ${system} tracking link`;
    return 'Open tracking-link documentation';
}

/** Query for the Help route: ?section=links-<system> when that section exists. */
export function helpQueryForSystem(system) {
    const slug = HELP_SLUG_BY_SYSTEM[normalizeSystem(system)];
    return slug ? { section: slug } : {};
}

// On these systems we create tracking links ourselves in the operator's panel, so
// a taken campaign key is fixed by making a new link. Everywhere else the operator
// issues links, so we have to ask their affiliate manager.
const SELF_SERVE_NEW_KEY = {
    myaffiliates: 'Create a new tracking link in the MyAffiliates panel (Marketing tab). Its media item id becomes the new campaign key.',
    softswiss: "Create a new promo in the operator's partner dashboard (Promos page). Its id becomes the new campaign key.",
};

export function newCampaignKeyGuidance(system) {
    const text = SELF_SERVE_NEW_KEY[normalizeSystem(system)];
    if (text) return { selfServe: true, text };
    return {
        selfServe: false,
        text: "Ask the operator's affiliate manager for a new tracking link for this brand. It comes with its own campaign key.",
    };
}

/** PUT /siteLinks/:id refused because the campaign key is already used on this login. */
export function isCampaignKeyTaken(err) {
    return err?.response?.status === 409 && err.response.data?.code === 'INTERNAL_NOTE_TAKEN';
}

export function getLinkParamsText(system, internalNote = '') {
    switch (normalizeSystem(system)) {
        case 'myaffiliates': {
            return `/${internalNote}/`;
        }
        case 'referon': {
            let pubid = internalNote || 'pubid';
            return `&pubid=${pubid}&clickid={clickid}`;
        }
        case 'softswiss': {
            return `?visit_id={clickid}`;
        }
        case 'affise': {
            return `&sub2=${internalNote || 'key'}&sub1={clickid}`;
        }
        case 'netrefer': {
            return `&subid=${internalNote || 'key'}-{clickid}`;
        }
        case 'netreferapi': {
            return `&campaign=${internalNote || 'key'}&clickid={clickid}`;
        }
        default:
            return '';
    }
}
